import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Column as TableColumn } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { ProgressBar } from 'primereact/progressbar'

import { WorkFileStatuses } from '../../_generated/api/workfile-import'
import { useWorkfileContext } from '../contexts/WorkfileContext'
import WorkfileConflictDialog from '../dialogs/WorkfileConflictDialog'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import CopyButton from '../common/pure/CopyButton'
import ContextHeader from '../common/ContextHeader'
import DateDisplay from '../common/pure/DateDisplay'
import Heading from '../common/pure/Heading'
import { Row } from '../common/pure/FlexContainer'
import { pi } from '../common/pure/PrimeIcon'
import { TooltipAvatar } from '../common/ProfileAvatar'
import TablePagination from '../table/TablePagination'
import Workfile from '../../models/Workfile'
import t from '../../tools/t'
import { DownloadButton, TableActionContainer } from '../table/TableButton'
import { StatusChip } from '../common/pure/chips'

const POLLINT_INTERVAL_MS = 5000

export default function WorkfilesScreen() {
    const { workfiles, refreshWorkfiles, uploadWorkfile, downloadWorkfile } = useWorkfileContext()
    const [conflictWorkfile, setConflictWorkfile] = useState<Workfile>()
    const uploadInputRef = useRef<HTMLInputElement>(null)

    const hideConflictDialog = () => setConflictWorkfile(undefined)

    const openUploadDialog = () => uploadInputRef.current?.click()

    const onUploadFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files
        if (!files) return
        await uploadWorkfile(Array.from(files))
        event.target.value = ''
    }

    useEffect(() => {
        refreshWorkfiles()
        const interval = setInterval(refreshWorkfiles, POLLINT_INTERVAL_MS)
        return () => clearInterval(interval)
    }, [])

    return (
        <LayoutColumnContainer>
            <LayoutRowContainer>
                <ContentColumn columns={3}>
                    <Row arrangement="spaced" alignment="start">
                        <ContextHeader>
                            <Heading standalone size={2}>
                                {t('common_workfiles')}
                            </Heading>
                        </ContextHeader>
                        <input
                            ref={uploadInputRef}
                            type="file"
                            accept=".jsonl"
                            onChange={onUploadFileSelected}
                            hidden
                            multiple={true}
                        />
                        <Button
                            label={t('workfile_upload_file')}
                            icon={pi('plus')}
                            onClick={openUploadDialog}
                        />
                    </Row>

                    {conflictWorkfile && (
                        <WorkfileConflictDialog
                            visible={!!conflictWorkfile}
                            workfile={conflictWorkfile}
                            onHide={hideConflictDialog}
                        />
                    )}

                    <Card>
                        <DataTable
                            dataKey="id"
                            value={workfiles.items}
                            emptyMessage={t('common_empty')}
                            rowHover={true}
                        >
                            <TableColumn
                                header={t('workfile_field_uploaded')}
                                body={(w: Workfile) => (
                                    <Row gap={0.5} alignment="center">
                                        <TooltipAvatar
                                            id={w.uploadedBy}
                                            target={w.id + w.uploadedBy}
                                        />
                                        <DateDisplay value={w.uploadedAt} target={w.id} />
                                    </Row>
                                )}
                            />

                            <TableColumn
                                header={t('common_file')}
                                body={(w: Workfile) => (
                                    <CopyButton copyString={w.id}>{w.id.slice(-8)}</CopyButton>
                                )}
                            />

                            <TableColumn
                                header={t('common_status')}
                                body={(w: Workfile) => <StatusChip status={w.status} />}
                            />

                            <TableColumn
                                header={t('common_progress')}
                                body={(w: Workfile) => w.processedRecords + '/' + w.totalRecords}
                            />

                            <TableColumn
                                align="left"
                                body={(w: Workfile) => (
                                    <>
                                        {w.status === WorkFileStatuses.Conflict && (
                                            <Button
                                                text
                                                size="small"
                                                label={t('workfile_resolve_conflict')}
                                                icon={pi('arrow-right')}
                                                iconPos="right"
                                                severity="danger"
                                                onClick={() => setConflictWorkfile(w)}
                                            />
                                        )}
                                        {w.status === WorkFileStatuses.Processing && (
                                            // ProgressBar total is always 100
                                            <ProgressBar
                                                className="table-cell-progress-bar"
                                                value={w.progress * 100}
                                            />
                                        )}
                                    </>
                                )}
                            />

                            <TableColumn
                                body={(w: Workfile) => (
                                    <TableActionContainer>
                                        <DownloadButton
                                            target={w.id}
                                            onClick={() => downloadWorkfile(w.id)}
                                        />
                                    </TableActionContainer>
                                )}
                            />
                        </DataTable>
                        <TablePagination data={workfiles} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </LayoutColumnContainer>
    )
}
